'use client';

import React, {
  ReactNode,
  useEffect,
  useState,
  useCallback,
  createContext,
  useContext,
} from 'react';

const reCaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_INVISIBLE;

declare global {
  interface Window {
    handleRecaptchaCallback: (token: string) => void;
    grecaptcha: {
      reset: () => void;
      execute: () => void;
    };
  }
}

interface RecaptchaProviderProps {
  children: ReactNode;
}

type RecaptchaValue = string;

const RecaptchaContext = createContext<RecaptchaValue | null>(null);

export const RecaptchaProvider = ({ children }: RecaptchaProviderProps) => {
  const [reCaptchaToken, setReCaptchaToken] = useState<string | null>(null);

  const handleRecaptchaCallback = useCallback((token: string) => {
    setReCaptchaToken(token);
  }, []);

  useEffect(() => {
    window.handleRecaptchaCallback = handleRecaptchaCallback;
  }, []);

  return (
    <>
      <RecaptchaContext.Provider value={reCaptchaToken}>{children}</RecaptchaContext.Provider>
      <div
        className="g-recaptcha"
        data-sitekey={reCaptchaKey}
        data-callback="handleRecaptchaCallback" // 讓 Google reCaptcha 設定 reCaptchaToken state
        data-size="invisible"
        style={{ display: 'none' }}
      />
    </>
  );
};

interface RecaptchaHookProps {
  onEvent?: (event: string, data: any) => void;
}

interface RecaptchaHookReturn {
  token: string | null;
  reset: () => void;
  execute: () => void;
}

export const useRecaptcha = ({ onEvent }: RecaptchaHookProps): RecaptchaHookReturn => {
  const token = useContext(RecaptchaContext);
  const [grecaptcha, setGrecaptcha] = useState<Window['grecaptcha'] | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      onEvent?.('interval', {});
      if (window.grecaptcha && typeof window.grecaptcha?.execute === 'function') {
        clearInterval(interval);
        setGrecaptcha(window.grecaptcha);
        onEvent?.('set window.grecaptcha', {});
        onEvent?.('reCaptcha before execute', {});
        try {
          window.grecaptcha.execute();
          onEvent?.('reCaptcha after execute', {});
        } catch (error) {
          onEvent?.('reCaptcha execute error', { error: (error as Error).toString() });
        }
      }
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const reset = () => {
    console.log('reCaptcha Reset');
    onEvent?.('reCaptcha before reset', {});
    try {
      grecaptcha?.reset();
      onEvent?.('reCaptcha after reset', {});
    } catch (error) {
      onEvent?.('reCaptcha reset error', { error: (error as Error).toString() });
    }
  };

  const execute = () => {
    console.log('reCaptcha Execute');
    onEvent?.('reCaptcha before execute', {});
    try {
      grecaptcha?.execute();
      onEvent?.('reCaptcha after execute', {});
    } catch (error) {
      onEvent?.('reCaptcha execute error', { error: (error as Error).toString() });
    }
  };

  return { token, reset, execute };
};
